import React, { useEffect, useState } from "react"
import { Film } from "lucide-react"
import { proxiedImageUrl, needsImageProxy } from "../utils"

// Same "Instagram gradient" used across the product for IG-branded empty
// states, used here as the placeholder background so a reel that truly can't
// be previewed still reads as "Instagram content", not as a generic error.
const IG_GRADIENT = "linear-gradient(135deg, #405DE6, #5B51D8, #833AB4, #C13584, #E1306C, #FD1D1D)"

/**
 * <img> wrapper for UGC thumbnails that degrades gracefully instead of
 * leaving the browser's default broken-image glyph (plus its spilled `alt`
 * text) sitting on the card. YouTube's thumbnails (i.ytimg.com) and this
 * app's own S3-hosted Instagram mirrors (`thumb_url`/`profile_thumb_url` -
 * see normalize.js) load directly; a raw Instagram CDN URL hotlink-protects
 * itself and rejects a direct browser request outright, so only that case
 * (detected via `needsImageProxy`) goes through `proxiedImageUrl` instead. If
 * even that fails (or there's no URL at all), this renders a designed
 * placeholder tile rather than nothing/broken-image art.
 */
const Thumbnail = ({ src, alt, isInstagram, style, loading }) => {
  const attempts = (needsImageProxy(src) ? [proxiedImageUrl(src)] : [src]).filter(Boolean)
  const [attemptIndex, setAttemptIndex] = useState(0)
  const [failed, setFailed] = useState(attempts.length === 0)

  // A new card (different reel/video) needs to re-attempt from scratch -
  // otherwise a previously-failed slot reused by the grid would stay stuck
  // showing the placeholder for whatever loaded fine before it.
  useEffect(() => {
    setAttemptIndex(0)
    setFailed(attempts.length === 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, isInstagram])

  if (failed) {
    return (
      <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: IG_GRADIENT }}>
        <Film size={isInstagram ? 34 : 26} color="rgba(255,255,255,0.85)" />
      </div>
    )
  }

  return (
    <img
      src={attempts[attemptIndex]}
      alt={alt}
      loading={loading}
      style={style}
      onError={() => {
        if (attemptIndex < attempts.length - 1) setAttemptIndex((i) => i + 1)
        else setFailed(true)
      }}
    />
  )
}

export default Thumbnail
